import { computed, makeObservable } from 'mobx';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getCopyrightFooter';
import { ResourceSSR } from 'src_common/common/mobx-utils/ResourceSSR';
import { Common } from 'src/domains/common/Common';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { Resource } from 'src_common/common/mobx-utils/Resource';

export class FooterCopyrightState {
    private readonly footerCopyrightResource: ResourceSSR<Response200Type | null> | Resource<Response200Type | null>;
    public static get = AutoWeakMap.create((common: Common) => new FooterCopyrightState(common));

    private constructor(private readonly common: Common) {
        makeObservable(this);
        const config = ConfigComponents.get(this.common).config;
        const callback = async (): Promise<Response200Type | null> => {
            const response = await this.common.trpcClient.client.cms.getFooterCopyrightText.query();

            if (response.status === 200) {
                return response.body;
            }

            if (response.status === 404) {
                return null;
            }

            console.error(`Unexpected http response code=${response.status}`, response);
            return null;
        };

        this.footerCopyrightResource = config.resourceSSR
            ? new ResourceSSR(callback, {
                  defaultValue: null,
                  label: 'footerCopyrightResource',
                  disableSSRFetch: true,
              })
            : new Resource(callback);
    }

    private get data(): Omit<Response200Type, 'universe'> {
        const data = this.footerCopyrightResource.get();
        const config = ConfigComponents.get(this.common).config;

        if (data.type === 'ready' && data.value !== null) {
            return data.value;
        }

        return config.footerCopyrightTemporaryDefaultMock;
    }

    @computed public get licenceNumber(): string {
        return this.data.licence_number;
    }

    @computed public get licenceUrl(): string | null {
        return this.data.licence_url ?? null;
    }

    @computed private get descriptionTextInner(): string {
        return this.data.description_text ?? '';
    }

    @computed public get copyrightDescription(): Array<string> | string {
        if (this.descriptionTextInner.includes('{{licenceNumber}}')) {
            return this.descriptionTextInner.split('{{licenceNumber}}');
        }

        return this.descriptionTextInner;
    }
}
